import { useNavigate } from 'react-router-dom'
import ArchiveIcon from '@mui/icons-material/Archive'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditNoteIcon from '@mui/icons-material/EditNote'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import PublishIcon from '@mui/icons-material/Publish'
import UndoIcon from '@mui/icons-material/Undo'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'

import { ArticleStatusChip } from '../../article/components/ArticleStatusChip'
import { useAuth } from '../../auth/hooks/useAuth'
import { RejectArticleDialog } from './RejectArticleDialog'
import { RequestChangesDialog } from './RequestChangesDialog'
import { WorkflowMetrics } from './WorkflowMetrics'
import { useAdminWorkflow } from '../hooks/useAdminWorkflow'

export function WorkflowTab() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const {
    articles,
    archiveDialog,
    handleAssignReviewer,
    handleApprove,
    handleArchive,
    handlePublish,
    handleReject,
    handleRequestChanges,
    loading,
    metrics,
    rejectComment,
    rejectDialog,
    requestDialog,
    requestForm,
    setRejectComment,
    setArchiveDialog,
    setRejectDialog,
    setRequestDialog,
    setRequestForm,
    setSnackbar,
    setStateFilter,
    snackbar,
    stateFilter,
  } = useAdminWorkflow()

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <WorkflowMetrics metrics={metrics} />

      <Paper sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h6">Review Queue</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by State</InputLabel>
            <Select value={stateFilter} label="Filter by State" onChange={event => setStateFilter(event.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="review">In Review</MenuItem>
              <MenuItem value="changes_requested">Changes Requested</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              {user?.role === 'admin' && <MenuItem value="archived">Archived</MenuItem>}
            </Select>
          </FormControl>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Discipline</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Reviewer</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : articles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No technotes found
                  </TableCell>
                </TableRow>
              ) : (
                articles.map(article => {
                  const isAdmin = user?.role === 'admin'
                  const isAssignedToCurrentUser = Boolean(user && article.reviewer_id === user.id)
                  const hasReviewer = Boolean(article.reviewer_id)
                  const canClaimReview = Boolean(user && article.state === 'review' && !hasReviewer)
                  const canReview = Boolean(article.state === 'review' && (isAdmin || isAssignedToCurrentUser))
                  const canArchive = Boolean(isAdmin && article.state === 'published')

                  return (
                    <TableRow key={article.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{article.technote_id || `#${article.id}`}</TableCell>
                      <TableCell sx={{ fontWeight: 600, maxWidth: 220 }}>{article.title}</TableCell>
                      <TableCell>{article.customer || '—'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{article.discipline || '—'}</TableCell>
                      <TableCell>{article.author_name}</TableCell>
                      <TableCell>{article.reviewer_name || '—'}</TableCell>
                      <TableCell>
                        <ArticleStatusChip state={article.state} />
                      </TableCell>
                      <TableCell>{new Date(article.updated_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => navigate(`/articles/${article.id}`)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canClaimReview && (
                          <Tooltip title="Assign to me">
                            <IconButton
                              size="small"
                              sx={{ color: '#0f4c81' }}
                              onClick={() => user && handleAssignReviewer(article.id, user.id)}
                            >
                              <PersonAddAlt1Icon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canReview && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton size="small" sx={{ color: '#198754' }} onClick={() => handleApprove(article.id)}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Request Changes">
                              <IconButton
                                size="small"
                                sx={{ color: '#b7791f' }}
                                onClick={() => setRequestDialog({ open: true, articleId: article.id })}
                              >
                                <EditNoteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject / Return to Draft">
                              <IconButton
                                size="small"
                                sx={{ color: '#b42318' }}
                                onClick={() => {
                                  setRejectDialog({ open: true, articleId: article.id })
                                  setRejectComment('')
                                }}
                              >
                                <UndoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {article.state === 'approved' && (
                          <Tooltip title="Publish">
                            <IconButton size="small" sx={{ color: '#0f4c81' }} onClick={() => handlePublish(article.id)}>
                              <PublishIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canArchive && (
                          <Tooltip title="Archive / remove Wiki.js access">
                            <IconButton
                              size="small"
                              sx={{ color: '#b42318' }}
                              onClick={() => setArchiveDialog({ open: true, articleId: article.id })}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <RequestChangesDialog
        open={requestDialog.open}
        requestForm={requestForm}
        onClose={() => setRequestDialog({ open: false, articleId: null })}
        onSubmit={handleRequestChanges}
        setRequestForm={setRequestForm}
      />

      <RejectArticleDialog
        open={rejectDialog.open}
        rejectComment={rejectComment}
        onClose={() => setRejectDialog({ open: false, articleId: null })}
        onReject={handleReject}
        setRejectComment={setRejectComment}
      />

      <Dialog open={archiveDialog.open} onClose={() => setArchiveDialog({ open: false, articleId: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Archive published technote?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes the published page from Wiki.js access first, then keeps the technote archived in the portal for history and audit records.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialog({ open: false, articleId: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void handleArchive()}>
            Archive Technote
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(current => ({ ...current, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  )
}
